import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { UserProfile } from '@/features/user/types/types';
import { INITIAL_USER_DATA } from '@/features/user/constants';
import { profileService } from '@/features/user/services/profileService';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'react-hot-toast';
import { getSignedMediaUrl } from '@/lib/utils';

export const useProfile = () => {
  const { user: authUser, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserProfile>(INITIAL_USER_DATA);
  const [isEditing, setIsEditing] = useState(false);
  const [profilePic, setProfilePic] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Update local user state when auth user changes
  useEffect(() => {
    if (authUser) {
      setUser({
        first_name: authUser.first_name,
        last_name: authUser.last_name,
        dob: authUser.dob,
        phone_number: authUser.phone_number,
        gender: authUser.gender || ''
      });
    }
  }, [authUser]);

  // Load profile picture when profile_picture_id is available
  useEffect(() => {
    if (authUser?.profile_picture_id && authUser.profile_picture_id !== '00000000-0000-0000-0000-000000000000') {
      try {
        // Use profileService method which uses getSignedMediaUrl internally
        const profilePicUrl = profileService.getProfilePictureUrl(authUser.profile_picture_id);
        if (profilePicUrl) {
          setProfilePic(profilePicUrl);
        } else {
          setProfilePic(null);
        }
      } catch (error) {
        console.error('Error loading profile picture:', error);
        setProfilePic(null);
      }
    } else {
      setProfilePic(null);
    }
  }, [authUser?.profile_picture_id]);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    // Proactive check: Skip if not authenticated
    if (!authLoading && !isAuthenticated) {
      toast.error('Please log in to upload a profile picture');
      navigate('/login');
      return;
    }

    if (authLoading) return;

    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Show preview immediately
      setProfilePic(URL.createObjectURL(file));

      try {
        // Upload the file using the new flow
        const profilePictureId = await profileService.uploadProfilePicture(file);

        // Update the profile picture URL using the signed media URL utility
        const signedUrl = getSignedMediaUrl(profilePictureId, 'image');
        setProfilePic(signedUrl.url);

        toast.success('Profile picture uploaded successfully!');
      } catch (error) {
        console.error('Error uploading profile picture:', error);
        toast.error('Failed to upload profile picture. Please try again.');
        // Reset to no profile picture on error
        setProfilePic(null);
      }
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleSaveChanges = useCallback(async (userData: UserProfile) => {
    // Proactive check: Skip if not authenticated
    if (!authLoading && !isAuthenticated) {
      toast.error('Please log in to update your profile');
      navigate('/login');
      return;
    }

    if (authLoading) return;

    setLoading(true);
    try {
      const response = await profileService.updateProfile(userData);

      // Update local user state with the response data
      if (response.data && response.data['public-user']) {
        const updatedUser = response.data['public-user'];
        setUser({
          first_name: updatedUser.first_name,
          last_name: updatedUser.last_name,
          dob: updatedUser.dob,
          phone_number: updatedUser.phone_number,
          gender: updatedUser.gender
        });
      }

      toast.success('Profile updated successfully!');
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleCancel = useCallback(() => {
    setUser(INITIAL_USER_DATA);
    setIsEditing(false);
  }, []);

  const startEditing = useCallback(() => {
    setIsEditing(true);
  }, []);

  return {
    user,
    setUser,
    isEditing,
    setIsEditing,
    profilePic,
    setProfilePic,
    loading,
    handleFileChange,
    handleSaveChanges,
    handleCancel,
    startEditing,
  };
};

